import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch } from 'react-redux';
import DJPalAudioPreview from './DJPalAudioPreview';
import { useMixPlayback, mixTrackingKey } from './MixPlayer';
import { goArtist, goMix, goMixesPlayingTrack, goTrack, homeRowCache } from './mixpediaHomeData';
import CoverArt from '../CoverArt';
import FeedWaveform from '../FeedWaveform';
import FakeTrackWaveform from '../FakeTrackWaveform';
import LikeButton from '../LikeButton';
import TrackContextMenu from '../TrackContextMenu';
import MultiArtistText from '../MultiArtistText';
import EntityHoverCard from '../EntityHoverCard';
import AddToPlaylistModal from '../AddToPlaylistModal';
import { MixFeedCard } from '../Tabs/FeedTab';
import { useFeedWaveformMode } from '../../utils/feedWaveform';
import { addToNextUp, fullFirstQueueSrc } from '../../utils/queueAdd';
import { entityId } from '../../utils/entityId';
import { setPendingStart } from '../../redux/slices/whatsNextSlice';
import { setActiveTab } from '../../redux/slices/appSlice';

// DESIGN V2 — the list view of a Mixpedia home row's "See all" page: the
// For You cards (play disc, waveform, actions) instead of a grid of covers,
// so the row can be listened through from here. The grid stays one click
// away (ViewToggle on the page).

const sampleOf = (t) => t.sampleUrl || t.previewUrl || t.preview_url || t.preview || null;

// Why the track is in this row, in the For You reason slot.
const TrackReason = ({ kind, item }) => {
    const dispatch = useDispatch();
    if (kind === 'artistTracks' && item.recentPlays > 0) {
        return (
            <>
                Played recently in{' '}
                <span className="feed-inline-link" onClick={(e) => { e.stopPropagation(); goMixesPlayingTrack(dispatch, item, homeRowCache.artistTracks.meta?.windowDays || 90); }}>
                    {item.recentPlays === 1 ? '1 mix' : `${item.recentPlays} mixes`}
                </span>
            </>
        );
    }
    if (kind === 'tracks' && item.mixDj) {
        const openMix = (e) => { e.stopPropagation(); goMix(dispatch, { id: item.mixId, publicId: item.mixPublicId, djName: item.mixDj, title: item.mixTitle }, 'browse'); };
        return (
            <>
                Played by{' '}
                <EntityHoverCard type="artist" entity={{ name: item.mixDj }} onNavigate={() => goArtist(dispatch, item.mixDj, null, null, 'browse')}>
                    <span className="feed-inline-link" onClick={(e) => { e.stopPropagation(); goArtist(dispatch, item.mixDj, null, null, 'browse'); }}>{item.mixDj}</span>
                </EntityHoverCard>
                {item.mixTitle && <>{' '}in{' '}<EntityHoverCard type="mix" entity={{ title: item.mixTitle, publicId: item.mixPublicId, djName: item.mixDj }} onNavigate={() => goMix(dispatch, { id: item.mixId, publicId: item.mixPublicId, djName: item.mixDj, title: item.mixTitle }, 'browse')}><span className="feed-inline-link" onClick={openMix}>{item.mixTitle}</span></EntityHoverCard></>}
            </>
        );
    }
    return null;
};

const BrowseTrackCard = ({ kind, item, index, items, onAddToPlaylist }) => {
    const dispatch = useDispatch();
    const waveformMode = useFeedWaveformMode();
    const [copied, setCopied] = useState(false);
    const previewId = `browse-${kind}-${index}`;
    const art = item.artworkUrl || item.artwork || null;
    const src = sampleOf(item);
    const youtubeId = item.youtubeId && item.youtubeId !== '-1' ? item.youtubeId : null;
    const play = (e) => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('djpal-row-play', { detail: previewId })); };
    const open = (e) => { e.stopPropagation(); goTrack(dispatch, item, 'browse'); };
    const copy = (e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(item.artist ? `${item.artist} - ${item.title}` : (item.title || '')).catch(() => {});
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };
    const queue = (e) => {
        e.stopPropagation();
        const s = fullFirstQueueSrc(item);
        if (s) addToNextUp(dispatch, { name: item.title || 'Unknown Track', singer: item.artist || '', cover: art || '', ...s });
    };
    const startSet = (e) => {
        e.stopPropagation();
        const title = (item.title || '').trim();
        if (!title) return;
        const artist = (item.artist || '').trim();
        dispatch(setPendingStart({ query: artist ? `${artist} - ${title}` : title, meta: { title, artist, artworkUrl: art } }));
        dispatch(setActiveTab('whats-next'));
    };
    return (
        <div className="feed-card">
            <div className="feed-card-body">
                <div className="feed-card-artwork feed-clickable" onClick={open}>
                    {art ? <CoverArt src={art} size={120} alt={item.title} /> : <div className="feed-artwork-placeholder" aria-hidden="true" />}
                </div>
                <div className="feed-card-content">
                    <div className="feed-card-track-info">
                        <DJPalAudioPreview
                            src={src}
                            youtubeId={youtubeId}
                            soundcloudUrl={item.soundcloudUrl || null}
                            trackId={entityId(item)}
                            durationSec={item.durationSec ?? item.duration}
                            pageUrl={item.pageUrl}
                            previewId={previewId}
                            trackName={item.title}
                            trackArtist={item.artist}
                            trackArtists={item.artists}
                            trackCover={art}
                            playContext={{ items, index, type: 'tracks' }}
                            playbackBlocked={item.playbackBlocked}
                        >
                            <button className="feed-play-btn" onClick={play}>
                                <svg className="feed-icon-play" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><polygon points="6 3 20 12 6 21 6 3" /></svg>
                                <svg className="feed-icon-pause" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><rect x="5" y="3" width="5" height="18" rx="1" /><rect x="14" y="3" width="5" height="18" rx="1" /></svg>
                            </button>
                        </DJPalAudioPreview>
                        <div className="feed-card-text">
                            <span className="feed-card-title feed-clickable" onClick={open}>{item.title || 'Unknown Track'}</span>
                            {(item.artist || item.artists?.length > 0) && (
                                <MultiArtistText
                                    artists={item.artists}
                                    fallbackText={item.artist}
                                    onSelect={(a) => goArtist(dispatch, a.name, a.id || null, a.artworkUrl || null, 'browse')}
                                    className="feed-card-artist"
                                    segmentClassName="feed-clickable"
                                    segmentWrapper={(seg, node) => (
                                        <EntityHoverCard type="artist" entity={seg} onNavigate={() => goArtist(dispatch, seg.name, seg.id || null, seg.artworkUrl || null, 'browse')}>
                                            {node}
                                        </EntityHoverCard>
                                    )}
                                />
                            )}
                        </div>
                    </div>
                    {/* As in For You: a placeholder until the track is played, so
                        opening the list downloads nothing. */}
                    <div className={`feed-card-waveform-wrapper${waveformMode === 'FAKE' ? ' feed-card-waveform-mix' : ''}`} onClick={waveformMode === 'FAKE' ? play : undefined}>
                        {waveformMode === 'FAKE' && <FakeTrackWaveform seed={index + 1} />}
                        <FeedWaveform src={src} youtubeId={youtubeId} soundcloudUrl={item.soundcloudUrl || null} trackId={entityId(item)} index={index} previewId={previewId} playbackBlocked={item.playbackBlocked} mode={waveformMode} />
                    </div>
                </div>
            </div>
            <div className="feed-card-actions">
                <LikeButton track={item} />
                <button type="button" className="feed-action-btn" title="Copy track name" onClick={copy}>
                    {copied
                        ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                        : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>}
                </button>
                <button type="button" className="feed-action-btn" title="Add to playlist" onClick={(e) => { e.stopPropagation(); onAddToPlaylist(item); }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="5" x2="21" y2="5" /><line x1="3" y1="11" x2="21" y2="11" /><line x1="3" y1="17" x2="12" y2="17" /><line x1="18" y1="14" x2="18" y2="22" /><line x1="14" y1="18" x2="22" y2="18" /></svg>
                </button>
                <button type="button" className="feed-action-btn feed-queue-btn" title="Add to Next Up" onClick={queue}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="12" y1="8" x2="12" y2="16" /><line x1="8" y1="12" x2="16" y2="12" /></svg>
                </button>
                <button type="button" className="feed-action-btn" title="Start a set with this track" onClick={startSet}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="5" cy="12" r="2.5" /><path d="M7.5 12 H20" /><path d="M16 8l4 4-4 4" /></svg>
                </button>
                <TrackContextMenu track={item} />
                <span className="feed-reason-inline"><TrackReason kind={kind} item={item} /></span>
            </div>
        </div>
    );
};

const MixpediaBrowseList = ({ kind, items }) => {
    const dispatch = useDispatch();
    const { playMix, queueMix, loadingMixId, playingMixId } = useMixPlayback();
    const [copiedId, setCopiedId] = useState(null);
    const [playlistTrack, setPlaylistTrack] = useState(null);

    let list;
    if (kind === 'sets') {
        // The home rows carry the For You mix fields under other names.
        const mixes = items.map(m => ({ ...m, dj: m.dj || m.djName || m.artist || '', djName: m.djName || m.dj || m.artist || '' }));
        list = mixes.map((mix, i) => {
            const key = mixTrackingKey(mix);
            return (
                <MixFeedCard
                    key={key || `mix-${i}`}
                    mix={mix}
                    index={i}
                    onOpen={(m) => goMix(dispatch, m, 'browse')}
                    onCopy={(text, id) => { navigator.clipboard.writeText(text).catch(() => {}); setCopiedId(id); setTimeout(() => setCopiedId(null), 1500); }}
                    copiedId={copiedId}
                    onPickTrack={(t) => goTrack(dispatch, t, 'browse')}
                    onPickArtist={(a) => goArtist(dispatch, typeof a === 'string' ? a : (a?.name || ''), typeof a === 'object' ? (a?.id || null) : null, typeof a === 'object' ? (a?.artworkUrl || null) : null, 'browse')}
                    onPlay={(m, ctx) => playMix(m, ctx)}
                    onQueue={(m) => queueMix(m)}
                    onAddToPlaylist={(e, t) => { e?.stopPropagation?.(); setPlaylistTrack(t); }}
                    isLoading={loadingMixId === key}
                    isPlaying={playingMixId === key}
                    playContext={{ items: mixes, index: i }}
                />
            );
        });
    } else {
        list = items.map((item, i) => (
            <BrowseTrackCard key={`${entityId(item) || item.title}-${i}`} kind={kind} item={item} index={i} items={items} onAddToPlaylist={setPlaylistTrack} />
        ));
    }

    return (
        <>
            <div className="feed-cards mh-browse-list">{list}</div>
            {playlistTrack && ReactDOM.createPortal(
                <AddToPlaylistModal show={!!playlistTrack} onHide={() => setPlaylistTrack(null)} track={playlistTrack} />,
                document.body,
            )}
        </>
    );
};

export default MixpediaBrowseList;
